"use client";
import { AnimatePresence, motion } from "framer-motion";
import { usePathname } from "next/navigation";
import { useEffect } from "react";
import AudioPlayerFull from "./AudioPlayerFull";
import AudioPlayerMini from "./AudioPlayerMini";
import { useAudioControls, useAudioMiniPlayerState } from "./useAudio";

import Box from "@mui/material/Box";
import { SITE_CONFIG } from "~/lib/constants";

const IGNORED_PATHS = ["/comanda"]; // paths where the mini player should not be shown and audio should be paused

export default function AudioPlayerBar() {
  const { pause } = useAudioControls();
  const { currentSong, playlist, bigPlayerOpen } =
    useAudioMiniPlayerState();
  const pathname = usePathname();

  useEffect(() => {
    // Stop audio when navigating to ignored paths, as the player is not shown there
    if (IGNORED_PATHS.some((path) => pathname.startsWith(path))) {
      pause();
    }
  }, [pathname, pause]);

  useEffect(() => {
    // add class overflow-y-hidden to body when big player is open to prevent background scrolling
    if (bigPlayerOpen) {
      document.body.classList.add("overflow-y-hidden");
    } else {
      document.body.classList.remove("overflow-y-hidden");
    }

    // Cleanup on unmount
    return () => {
      document.body.classList.remove("overflow-y-hidden");
    };
  }, [bigPlayerOpen]);

  useEffect(() => {
    if (!currentSong) return;
    if (bigPlayerOpen) {
      document.title = currentSong.title + " - " + currentSong.description + " | " + SITE_CONFIG.name;
    }
  }, [bigPlayerOpen, currentSong]);

  if (!currentSong) return null;
  if (IGNORED_PATHS.some((path) => pathname.startsWith(path))) {
    return null;
  }

  const fullVariants = {
    hidden: { y: "100%", opacity: 0 },
    visible: { y: 0, opacity: 1, transition: { stiffness: 300, damping: 30 } },
    exit: { y: "100%", opacity: 0, transition: { duration: 0.18 } },
  } as const;

  const miniVariants = {
    hidden: { y: 0, opacity: 1 },
    visible: { y: 0, opacity: 1 },
    exit: { y: 0, opacity: 0, transition: { duration: 0.15 } },
  } as const;

  return (
    <>
      <AnimatePresence>
        {bigPlayerOpen && (
          <Box
            component={motion.div}
            key="full-player"
            initial="hidden"
            animate="visible"
            exit="exit"
            variants={fullVariants}
            sx={{ position: "fixed", inset: 0, zIndex: 1300 }}
          >
            <Box sx={{ height: "100vh", overflow: "auto" }}>
              <AudioPlayerFull song={currentSong} playlist={playlist} />
            </Box>
          </Box>
        )}
      </AnimatePresence>

      <Box sx={{ height: 64 }} />

      <AnimatePresence>
        {!bigPlayerOpen && (
          <Box
            component={motion.div}
            key="mini-player"
            initial="hidden"
            animate="visible"
            exit="exit"
            variants={miniVariants}
            sx={{
              position: "fixed",
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 1200,
            }}
          >
            <AudioPlayerMini />
          </Box>
        )}
      </AnimatePresence>
    </>
  );
}
